import { useState } from "react";
import { BAY_ISLE, TERRACOTTA, SEMI_SWEET, PROPER_GREY, RULE, WHITE, FONT_DISPLAY, FONT_DISPLAY_HEAVY, FONT_SERIF, FONT_BODY } from "../theme";
import Testimonials from "../components/Testimonials";
import { TESTIMONIALS } from "../data/testimonials";

const AGENT_REVIEWS = TESTIMONIALS.filter(t => t.audience === "agent");

const LISTING_ADDONS = [
  { label: "Social graphics set (4 branded posts)", price: "+$75" },
  { label: "Digital flyer PDF", price: "+$50" },
  { label: "Printable 2-sided brochure", price: "+$100" },
  { label: "Instagram story graphics set (3)", price: "+$60" },
  { label: "Email blast copy", price: "+$40" },
  { label: "Google Business Profile post", price: "+$35" },
  { label: "Open house graphics set", price: "+$60" },
  { label: "Rush delivery (24 hrs)", price: "+$75" },
];

const LISTING_INCLUDES = [
  "4 AI virtually staged photos",
  "AI-written MLS listing description",
  "4 social captions with hooks + hashtags",
  "5 reel hook & headline options",
  "Auto-captioned vertical reel",
  "Google Drive delivery — 48 hrs",
];

function CustomForm() {
  const [cf, setCf] = useState({ name: "", email: "", message: "" });
  const [cfSubmitted, setCfSubmitted] = useState(false);
  const [cfError, setCfError] = useState("");
  const [cfLoading, setCfLoading] = useState(false);

  const handleCf = async (e) => {
    e.preventDefault();
    if (!cf.name.trim() || !cf.email.trim() || !cf.message.trim()) { setCfError("Please fill in all fields."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cf.email)) { setCfError("Please enter a valid email address."); return; }
    setCfError(""); setCfLoading(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: Object.entries({ "form-name": "acadia-custom-request", "bot-field": "", full_name: cf.name.trim(), email: cf.email.trim(), message: cf.message.trim() })
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&"),
      });
      setCfSubmitted(true);
    } catch {
      setCfError("Something went wrong. Please try again or email us directly.");
    } finally {
      setCfLoading(false);
    }
  };

  if (cfSubmitted) {
    return (
      <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "1.75rem", textAlign: "center" }}>
        <p style={{ fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: 20, color: BAY_ISLE, marginBottom: 8 }}>Got it — we'll be in touch!</p>
        <p style={{ fontSize: 12.5, color: PROPER_GREY, fontFamily: FONT_BODY }}>We'll review your request and reach out within 1–2 business days.</p>
      </div>
    );
  }

  return (
    <form name="acadia-custom-request" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleCf} noValidate>
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value="acadia-custom-request" />
      <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "20px 18px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label>Full name *</label>
            <input type="text" name="full_name" placeholder="Jane Smith" value={cf.name} onChange={e => setCf(p => ({ ...p, name: e.target.value }))} autoComplete="name" />
          </div>
          <div>
            <label>Email address *</label>
            <input type="email" name="email" placeholder="jane@brokerage.com" value={cf.email} onChange={e => setCf(p => ({ ...p, email: e.target.value }))} autoComplete="email" />
          </div>
        </div>
        <div>
          <label>What do you need? *</label>
          <textarea name="message" placeholder="Describe your project — a custom graphic, a one-off reel, something else..." value={cf.message} onChange={e => setCf(p => ({ ...p, message: e.target.value }))} rows={4} />
        </div>
      </div>
      {cfError && (
        <p style={{ fontSize: 12, color: "#7a2a2a", background: "rgba(180,60,60,0.06)", border: "1px solid rgba(180,60,60,0.15)", borderRadius: 8, padding: "10px 14px", margin: "10px 0 0", fontFamily: FONT_BODY }}>
          {cfError}
        </p>
      )}
      <button type="submit" disabled={cfLoading} style={{ width: "100%", marginTop: 12 }}>
        {cfLoading ? "Sending..." : "Send custom request →"}
      </button>
    </form>
  );
}

function Check() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="6.5" cy="6.5" r="6" stroke={TERRACOTTA} strokeWidth="1" />
      <path d="M4 6.5l1.7 1.7L9 4.8" stroke={TERRACOTTA} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AddonRow({ label, price, checked, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
      borderRadius: 8, cursor: "pointer", userSelect: "none",
      background: checked ? "rgba(195,135,98,0.07)" : "transparent",
      border: checked ? `1px solid rgba(195,135,98,0.3)` : "1px solid transparent",
      transition: "all 0.13s",
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        border: checked ? "none" : `1.5px solid ${PROPER_GREY}`,
        background: checked ? TERRACOTTA : WHITE,
        display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.13s",
      }}>
        {checked && (
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
            <path d="M1.5 4.5l2 2L7.5 2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span style={{ flex: 1, fontSize: 12, color: SEMI_SWEET, lineHeight: 1.3, fontFamily: FONT_BODY }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: TERRACOTTA, flexShrink: 0 }}>{price}</span>
    </div>
  );
}

function SuccessScreen({ addons, name }) {
  const first = name ? name.trim().split(" ")[0] : null;
  return (
    <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
      <div style={{
        width: 56, height: 56, borderRadius: "50%", background: "rgba(195,135,98,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem",
      }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M4 11l4.5 4.5L18 6" stroke={TERRACOTTA} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <p style={{ fontFamily: FONT_SERIF, fontSize: 26, color: BAY_ISLE, marginBottom: 8, fontStyle: "italic" }}>
        Request received{first ? `, ${first}` : ""}!
      </p>
      <p style={{ fontSize: 13, color: PROPER_GREY, lineHeight: 1.65, maxWidth: 360, margin: "0 auto 16px", fontFamily: FONT_BODY }}>
        We got your AI Listing Boost request.
        {addons.length > 0 && ` We'll follow up on your ${addons.length} add-on${addons.length > 1 ? "s" : ""} as well.`}
        {" "}We'll be in touch within 1–2 business days to confirm everything.
      </p>
      {addons.length > 0 && (
        <div style={{
          background: "rgba(195,135,98,0.07)", border: `1px solid rgba(195,135,98,0.2)`,
          borderRadius: 10, padding: "12px 16px", maxWidth: 360, margin: "0 auto 16px", textAlign: "left",
        }}>
          <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: 7, fontFamily: FONT_BODY }}>
            Your selected add-ons
          </p>
          {addons.map((a, i) => <p key={i} style={{ fontSize: 11, color: SEMI_SWEET, marginBottom: 3, fontFamily: FONT_BODY }}>— {a}</p>)}
        </div>
      )}
      <div style={{
        display: "inline-block", fontSize: 12, color: PROPER_GREY,
        background: "rgba(33,62,68,0.05)", border: `1px solid ${RULE}`, borderRadius: 8, padding: "10px 18px",
      }}>
        Follow along → <span style={{ color: TERRACOTTA, fontWeight: 700 }}>@acadiamediamarketing</span>
      </div>
    </div>
  );
}

function encode(data) {
  return Object.entries(data).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}

export default function ForAgents() {
  const [addons, setAddons]         = useState([]);
  const [name, setName]             = useState("");
  const [email, setEmail]           = useState("");
  const [brokerage, setBrokerage]   = useState("");
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  const toggleAddon = (label) => setAddons(prev => prev.includes(label) ? prev.filter(a => a !== label) : [...prev, label]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError("Please fill in your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "acadia-waitlist", "bot-field": "",
          full_name: name.trim(), email: email.trim(), brokerage: brokerage.trim(),
          plan_selected: "AI Listing Boost", plan_price: "$299/listing",
          addons: addons.length > 0 ? addons.join(" | ") : "None",
        }),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "0 18px 4rem" }}>

      <div style={{ textAlign: "center", padding: "2.75rem 0 2rem" }}>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase",
          color: TERRACOTTA, borderBottom: `1.5px solid ${TERRACOTTA}`,
          display: "inline-block", paddingBottom: 4, marginBottom: 18, fontFamily: FONT_BODY,
        }}>
          AI Listing Boost
        </p>
        <h1 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(26px, 5.5vw, 40px)", color: BAY_ISLE, lineHeight: 1.12, marginBottom: 14, fontWeight: 400 }}>
          AI marketing for{" "}
          <em style={{ color: TERRACOTTA, fontStyle: "italic", fontFamily: FONT_SERIF, fontSize: "1.15em" }}>Hudson Valley</em>{" "}
          realtors
        </h1>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.75, maxWidth: 420, margin: "0 auto 8px", fontFamily: FONT_BODY }}>
          Virtual staging, listing copy, captions, and a reel — AI-produced and delivered in 48 hours. Select your add-ons and submit a request to get started.
        </p>
      </div>

      {/* Package summary card */}
      <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "20px 20px 18px", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: PROPER_GREY, marginBottom: 5, fontFamily: FONT_BODY }}>Base package</p>
            <p style={{ fontSize: 17, fontWeight: 700, color: BAY_ISLE, fontFamily: FONT_DISPLAY }}>AI Listing Boost</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ fontSize: 26, fontWeight: 400, color: BAY_ISLE, fontFamily: FONT_SERIF }}>$299</span>
            <span style={{ fontSize: 11, color: PROPER_GREY, display: "block" }}>/listing</span>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${RULE}`, paddingTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
          {LISTING_INCLUDES.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 4 }}>
              <Check />
              <span style={{ fontSize: 11, color: SEMI_SWEET, lineHeight: 1.4, fontFamily: FONT_BODY }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {submitted ? (
        <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "1rem" }}>
          <SuccessScreen addons={addons} name={name} />
        </div>
      ) : (
        <form name="acadia-waitlist" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} noValidate>
          <input type="hidden" name="bot-field" />
          <input type="hidden" name="form-name" value="acadia-waitlist" />
          <input type="hidden" name="plan_selected" value="AI Listing Boost" />
          <input type="hidden" name="plan_price" value="$299/listing" />

          <div style={{ marginBottom: "1.5rem" }}>
            <p className="step-label">
              Step 1 — À la carte upgrades
              <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10, color: PROPER_GREY }}> optional</span>
            </p>
            <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 12, padding: "12px 14px" }}>
              <p style={{ fontSize: 11, color: PROPER_GREY, marginBottom: 10, lineHeight: 1.55, fontFamily: FONT_BODY }}>
                The base package covers all AI-produced deliverables. Select any extras — no commitment, just flagging interest.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {LISTING_ADDONS.map((a, i) => (
                  <AddonRow key={i} label={a.label} price={a.price} checked={addons.includes(a.label)} onToggle={() => toggleAddon(a.label)} />
                ))}
              </div>
              {addons.length > 0 && (
                <div style={{ marginTop: 12, padding: "9px 12px", borderRadius: 7, background: BAY_ISLE, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>{addons.length} add-on{addons.length > 1 ? "s" : ""} selected</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: TERRACOTTA, fontFamily: FONT_BODY }}>$299 + add-ons</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <p className="step-label">Step 2 — Your details</p>
            <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 12, padding: "20px 18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label>Full name *</label>
                  <input type="text" name="full_name" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <label>Email address *</label>
                  <input type="email" name="email" placeholder="jane@brokerage.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                </div>
              </div>
              <div>
                <label>Brokerage <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <input type="text" name="brokerage" placeholder="Keller Williams, Compass, Howard Hanna..." value={brokerage} onChange={e => setBrokerage(e.target.value)} />
              </div>
              <input type="hidden" name="addons" value={addons.length > 0 ? addons.join(" | ") : "None"} />
            </div>
          </div>

          {error && (
            <p style={{ fontSize: 12, color: "#7a2a2a", background: "rgba(180,60,60,0.06)", border: "1px solid rgba(180,60,60,0.15)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: FONT_BODY }}>
              {error}
            </p>
          )}

          <button type="submit" disabled={loading} style={{ width: "100%" }}>
            {loading ? "Sending request..." : "Request Listing Package →"}
          </button>

          <p style={{ fontSize: 11, color: PROPER_GREY, textAlign: "center", marginTop: 9, lineHeight: 1.5, fontFamily: FONT_BODY }}>
            No payment now. We'll reach out within 1–2 business days to confirm your request.
          </p>
        </form>
      )}

      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "center", marginTop: "2.25rem", paddingTop: "1.75rem", borderTop: `1px solid ${RULE}` }}>
        {["AI-written listing copy", "Virtual staging included", "48-hr delivery", "Built for HV realtors", "No upfront payment"].map((t, i) => (
          <span key={i} style={{ fontSize: 11, fontWeight: 500, padding: "5px 11px", borderRadius: 999, background: WHITE, border: `1px solid ${RULE}`, color: PROPER_GREY, fontFamily: FONT_BODY }}>{t}</span>
        ))}
      </div>

      {/* CUSTOM WORK */}
      <div style={{ marginTop: "3rem", paddingTop: "2.5rem", borderTop: `1px solid ${RULE}` }}>
        <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: PROPER_GREY, marginBottom: 8, fontFamily: FONT_BODY }}>
            Something else in mind?
          </p>
          <p style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(18px, 3.5vw, 24px)", color: BAY_ISLE, fontWeight: 400, marginBottom: 10 }}>
            Custom project requests
          </p>
          <p style={{ fontSize: 13, color: SEMI_SWEET, lineHeight: 1.7, maxWidth: 420, margin: "0 auto", fontFamily: FONT_BODY }}>
            Need something outside the standard package — a one-off graphic, a custom reel, branded materials, or something we haven't listed? Tell us what you're working on.
          </p>
        </div>
        <CustomForm />
      </div>
    </div>
    <div style={{ background: WHITE, padding: "4rem 20px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: 8, fontFamily: FONT_BODY }}>
          Custom AI Builds
        </p>
        <h2 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(22px, 3.5vw, 30px)", color: BAY_ISLE, fontWeight: 400, marginBottom: 12, lineHeight: 1.2 }}>
          Need something built from scratch?
        </h2>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, maxWidth: 540, marginBottom: "2rem", fontFamily: FONT_BODY }}>
          Beyond the listing package, we build fully custom AI-powered tools and assets — priced by project. Every quote is unique.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: "2.5rem" }}>
          {[
            { icon: "ti-world", label: "AI-Powered Websites", desc: "Custom sites built around your brand and market." },
            { icon: "ti-calculator", label: "Interactive Tools & Calculators", desc: "Mortgage estimators, market snapshots, lead capture tools." },
            { icon: "ti-layout-dashboard", label: "Branded Dashboards", desc: "Market data and listing performance at a glance." },
            { icon: "ti-file-text", label: "Digital Assets & Artifacts", desc: "Branded PDFs, guides, presentations, and templates." },
          ].map((item, i) => (
            <div key={i} style={{ background: "rgba(33,62,68,0.03)", border: `1px solid ${RULE}`, borderRadius: 12, padding: "18px 16px" }}>
              <i className={`ti ${item.icon}`} style={{ fontSize: 20, color: TERRACOTTA, display: "block", marginBottom: 10 }} />
              <p style={{ fontSize: 13, fontWeight: 700, color: BAY_ISLE, marginBottom: 6, fontFamily: FONT_DISPLAY }}>{item.label}</p>
              <p style={{ fontSize: 12.5, color: SEMI_SWEET, lineHeight: 1.65, fontFamily: FONT_BODY }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <a href="/get-started" style={{ display: "inline-block", padding: "12px 24px", borderRadius: 9, background: BAY_ISLE, color: WHITE, textDecoration: "none", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", fontFamily: FONT_BODY }}>
          Request a custom quote →
        </a>
      </div>
    </div>
    <Testimonials reviews={AGENT_REVIEWS} dark={true} label="Agent stories" />
    </>
  );
}
